<template>
  <div>
    <div class="row padding-top">
      <div class="columns" v-html="contentModel.PersonRight" />
      <div class="columns text-center push-up" v-html="contentModel.TeethUpperMouth" />
      <div class="columns text-right" v-html="contentModel.PersonLeft" />
    </div>
    <div :style="topTeethBackground" class="square teeth-area teeth-area--top">
      <div v-for="(item, index) in upperTeethDamageList" :key="'upper-teeth-' + index">
        <label
          :for="'teeth-area--' + item.Id"
          :class="'checkbox-button-no-check teeth-area--' + [index + 1] + ' hide-for-small-only'"
        >
          <input
            :id="'teeth-area--' + item.Id"
            v-model="selectedDentalDamageZones"
            :value="item"
            name="teeth-area"
            type="checkbox"
            @update:model-value="handleChange"
          />
          <span class="button primary">{{ item.Description }}</span>
        </label>
        <span :class="'show-for-small-only teeth-area--' + [index + 1]">{{
          item.Description
        }}</span>
      </div>
    </div>

    <fieldset class="checkbox-inline show-for-small-only teeth-area--mobile">
      <div class="row">
        <div class="columns small-6">
          <div
            v-for="(item, index) in upperTeethDamageList.slice(0, 8)"
            :key="'upper-teeth-mobile-1-' + index"
          >
            <input
              :id="'damage-area--' + item.Id"
              v-model="selectedDentalDamageZones"
              :value="item"
              :data-test="'damage-area--' + item.Id"
              type="checkbox"
              @update:model-value="handleChange"
            />
            <label :for="'damage-area--' + item.Id">{{ item.Description }}</label>
          </div>
        </div>
        <div class="columns small-6">
          <div
            v-for="(item, index) in upperTeethDamageList.slice(8)"
            :key="'upper-teeth-mobile-2-' + index"
          >
            <input
              :id="'damage-area--' + item.Id"
              v-model="selectedDentalDamageZones"
              :value="item"
              :data-test="'damage-area--' + item.Id"
              type="checkbox"
              @update:model-value="handleChange"
            />
            <label :for="'damage-area--' + item.Id">{{ item.Description }}</label>
          </div>
        </div>
      </div>
    </fieldset>

    <div class="row padding-top">
      <div class="columns" v-html="contentModel.PersonRight" />
      <div class="columns text-center push-up" v-html="contentModel.TeethLowerMouth" />
      <div class="columns text-right" v-html="contentModel.PersonLeft" />
    </div>
    <div :style="bottomTeethBackground" class="square teeth-area teeth-area--bottom">
      <div v-for="(item, index) in lowerTeethDamageList" :key="'lower-teeth-' + index">
        <label
          :for="'teeth-area--' + item.Id"
          :class="'checkbox-button-no-check teeth-area--' + [index + 1] + ' hide-for-small-only'"
        >
          <input
            :id="'teeth-area--' + item.Id"
            v-model="selectedDentalDamageZones"
            :value="item"
            type="checkbox"
            @update:model-value="handleChange"
          />
          <span class="button primary">{{ item.Description }}</span>
        </label>
        <span :class="'show-for-small-only teeth-area--' + [index + 1]">{{
          item.Description
        }}</span>
      </div>
    </div>

    <fieldset class="checkbox-inline show-for-small-only teeth-area--mobile">
      <div class="row">
        <div class="columns small-6">
          <div
            v-for="(item, index) in lowerTeethDamageList.slice(0, 8)"
            :key="'lower-teeth-mobile-1-' + index"
          >
            <input
              :id="'damage-area--' + item.Id"
              v-model="selectedDentalDamageZones"
              :value="item"
              :data-test="'damage-area--' + item.Id"
              type="checkbox"
              @update:model-value="handleChange"
            />
            <label :for="'damage-area--' + item.Id">{{ item.Description }}</label>
          </div>
        </div>
        <div class="columns small-6">
          <div
            v-for="(item, index) in lowerTeethDamageList.slice(8)"
            :key="'lower-teeth-mobile-2-' + index"
          >
            <input
              :id="'damage-area--' + item.Id"
              v-model="selectedDentalDamageZones"
              :value="item"
              :data-test="'damage-area--' + item.Id"
              type="checkbox"
              @update:model-value="handleChange"
            />
            <label :for="'damage-area--' + item.Id">{{ item.Description }}</label>
          </div>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Utility from '@/common/UtilityFunctions.js';
import { BodyPart } from '../types/BodyPart.types';
import { DataModel } from './PersonalAccidentTeeth.types';
import { Step3Accident } from '@/interfaces/CreateClaimFlowViewModel';

export default defineComponent({
  name: 'PersonalAccidentBodyTeeth',
  // inject: ['$validator'],
  props: {
    modelValue: {
      type: Array as PropType<BodyPart[] | null>,
      default() {
        return [];
      }
    },
    contentModel: {
      type: Object as PropType<Step3Accident>,
      default() {
        return {
          StepTitle: '',
          PersonSecondTitle: '',
          PersonRight: '',
          PersonLeft: '',
          PersonRequiredErrorMessage: '',
          TeethSecondTitle: '',
          TeethUpperMouth: '',
          TeethLowerMouth: '',
          TeethDentistStatementNotice: '',
          TeethRequiredErrorMessage: '',
          LinkPersonalPrivacyURL: '',
          LinkPersonalPrivacyText: ''
        };
      }
    },
    teethDamageList: {
      type: Array as PropType<BodyPart[]>,
      default() {
        return [];
      }
    }
  },
  data(): DataModel {
    return {
      selectedDentalDamageZones: []
    };
  },
  computed: {
    upperTeethDamageList(): BodyPart[] {
      if (this.teethDamageList) {
        return this.teethDamageList.slice(0, 16);
      }
      return [];
    },
    lowerTeethDamageList(): BodyPart[] {
      if (this.teethDamageList) {
        return this.teethDamageList.slice(16, 32);
      }
      return [];
    },
    topTeethBackground(): string {
      return `background-image: url('/assets/portal/images/damagezones/${Utility.getBrandFolderName()}/TopTeeth.svg`;
    },
    bottomTeethBackground(): string {
      return `background-image: url('/assets/portal/images/damagezones/${Utility.getBrandFolderName()}/BottomTeeth.svg`;
    }
  },
  methods: {
    handleChange(val: BodyPart): void {
      this.$emit('update:modelValue', val);
    }
  },
  async mounted(): Promise<void> {
    await this.$nextTick();
    this.selectedDentalDamageZones = this.modelValue;
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all';
.square {
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}

.checkbox-button-no-check {
  [type='checkbox'] {
    & + span {
      width: 27px;
      height: 27px;
      padding: 0 33% !important;
      font-size: $small-font-size;
    }

    &:checked {
      & + span {
        background-color: $secondary-color;
        opacity: 0.9;
      }
    }
  }
}

$teeth-1: 31%;
$teeth-2: 36%;
$teeth-3: 41%;
$teeth-4: 45%;
$teeth-5: 48%;
$teeth-6: 51%;
$teeth-7: 53%;
$teeth-8: 54%;

.teeth-area {
  padding-bottom: 40%;
  background-size: contain;

  &--top {
    background-position-y: top;

    @include breakpoint(small only) {
      margin-bottom: -15%;
    }
  }

  &--bottom {
    margin-top: -15%;
    background-position-y: bottom;

    @include breakpoint(medium) {
      margin-bottom: 15%;
    }
  }

  > div {
    > label,
    > span {
      position: absolute;
    }
  }

  &--mobile {
    margin-top: 10%;
    margin-bottom: 20%;

    label {
      width: 100%;
    }
  }

  &--1 {
    top: $teeth-1;
    left: 0;
  }

  &--2 {
    top: $teeth-2;
    left: 7%;
  }

  &--3 {
    top: $teeth-3;
    left: 14%;
  }

  &--4 {
    top: $teeth-4;
    left: 20%;
  }

  &--5 {
    top: $teeth-5;
    left: 26%;
  }

  &--6 {
    top: $teeth-6;
    left: 33%;
  }

  &--7 {
    top: $teeth-7;
    left: 39%;
  }

  &--8 {
    top: $teeth-8;
    left: 45%;
  }

  &--9 {
    top: $teeth-8;
    left: 51%;
  }

  &--10 {
    top: $teeth-7;
    left: 57%;
  }

  &--11 {
    top: $teeth-6;
    left: 64%;
  }

  &--12 {
    top: $teeth-5;
    left: 70%;
  }

  &--13 {
    top: $teeth-4;
    left: 76%;
  }

  &--14 {
    top: $teeth-3;
    left: 83%;
  }

  &--15 {
    top: $teeth-2;
    left: 89%;
  }

  &--16 {
    top: $teeth-1;
    left: 95%;
  }
}
</style>
